import { useEffect, useRef, useState } from "react";
import { Camera, Check, Copy, ExternalLink, Loader2, Mail, Phone, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { updateCliente, setClienteLogo, addClienteEnlace, deleteClienteEnlace, getApiErrorMessage } from "@/lib/api";
import { compressImageFile } from "@/lib/compressImageFile";
import { nombreVisible, nombreLegal, datosFiscalesTexto } from "@/lib/clienteNombre";
import { ClienteLogo } from "@/components/clientes/ClienteLogo";
import { EnlaceChip } from "@/components/chat/Adjuntos";
import { cn } from "@/lib/utils";

async function copiar(texto, etiqueta = "Copiado") {
  try {
    await navigator.clipboard.writeText(texto);
    toast.success(etiqueta);
  } catch {
    toast.error("No se pudo copiar");
  }
}

/** Campo que se edita en el sitio y se guarda al salir (Enter o blur). Muestra botón de copiar. */
function Campo({ label, value, campo, onSave, href, icon: Icon, placeholder = "—", mono = false }) {
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(value || ""), [value]);

  const guardar = async () => {
    if ((draft || "").trim() === (value || "").trim()) return;
    setSaving(true);
    try {
      await onSave(campo, draft.trim());
    } catch {
      setDraft(value || "");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="group grid grid-cols-[96px_1fr_auto] items-center gap-2 min-h-[40px]">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={guardar}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setDraft(value || "");
            e.currentTarget.blur();
          }
        }}
        placeholder={placeholder}
        className={cn("h-9 border-transparent shadow-none bg-transparent hover:border-input focus-visible:border-input px-2 text-sm", mono && "font-mono")}
      />
      <div className="flex items-center gap-0.5 w-[64px] justify-end">
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
        {!saving && value && href && (
          <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground" title="Abrir">
            {Icon ? <Icon className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}
          </a>
        )}
        {!saving && value && (
          <button type="button" onClick={() => copiar(value, `${label} copiado`)} className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground opacity-60 md:opacity-0 group-hover:opacity-100 hover:bg-muted hover:text-foreground" title="Copiar">
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function Seccion({ titulo, accion, children }) {
  return (
    <section className="space-y-1">
      <div className="flex items-center justify-between h-8">
        <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{titulo}</h3>
        {accion}
      </div>
      {children}
    </section>
  );
}

/**
 * Ficha del cliente editable en el sitio. Se usa a página completa y en el panel lateral del chat.
 * `onChange(cliente)` recibe el cliente actualizado tras cada guardado.
 */
export function ClienteFicha({ cliente, onChange, compact = false }) {
  const logoInputRef = useRef(null);
  const [subiendoLogo, setSubiendoLogo] = useState(false);
  const [editAlias, setEditAlias] = useState(false);
  const [alias, setAlias] = useState(cliente.alias || "");
  const [notas, setNotas] = useState(cliente.notas || "");
  const [nuevoEnlace, setNuevoEnlace] = useState(null);

  useEffect(() => {
    setAlias(cliente.alias || "");
    setNotas(cliente.notas || "");
  }, [cliente.id, cliente.alias, cliente.notas]);

  const guardar = async (campo, valor) => {
    try {
      const actualizado = await updateCliente(cliente.id, { [campo]: valor });
      onChange?.(actualizado);
      return actualizado;
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo guardar"));
      throw err;
    }
  };

  const guardarAlias = async () => {
    setEditAlias(false);
    if ((alias || "").trim() === (cliente.alias || "")) return;
    try {
      await guardar("alias", alias.trim());
    } catch {
      setAlias(cliente.alias || "");
    }
  };

  const cambiarLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Elige una imagen");
      return;
    }
    setSubiendoLogo(true);
    try {
      const dataUrl = await compressImageFile(file, { maxEdge: 320, maxOutputBytes: 200 * 1024, skipBelowBytes: 60 * 1024 });
      onChange?.(await setClienteLogo(cliente.id, dataUrl));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo subir el logo"));
    } finally {
      setSubiendoLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const quitarLogo = async () => {
    try {
      onChange?.(await setClienteLogo(cliente.id, null));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo quitar el logo"));
    }
  };

  const guardarEnlace = async () => {
    if (!nuevoEnlace?.url?.trim()) return;
    try {
      const enlace = await addClienteEnlace(cliente.id, nuevoEnlace);
      onChange?.({ ...cliente, enlaces: [...(cliente.enlaces || []), enlace] });
      setNuevoEnlace(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo añadir el enlace"));
    }
  };

  const borrarEnlace = async (enlaceId) => {
    try {
      await deleteClienteEnlace(cliente.id, enlaceId);
      onChange?.({ ...cliente, enlaces: (cliente.enlaces || []).filter((e) => e.id !== enlaceId) });
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo borrar el enlace"));
    }
  };

  const legal = nombreLegal(cliente);
  const inactivo = cliente.estado === "inactivo";

  return (
    <div className={cn("space-y-6", compact ? "text-sm" : "")}>
      <div className="flex items-center gap-4">
        <div className="relative group shrink-0">
          <ClienteLogo cliente={cliente} size={compact ? 64 : 88} />
          <button
            type="button"
            onClick={() => logoInputRef.current?.click()}
            className="absolute inset-0 rounded-xl bg-black/50 text-white opacity-0 group-hover:opacity-100 focus-visible:opacity-100 flex items-center justify-center transition-opacity"
            title="Cambiar logo"
          >
            {subiendoLogo ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
          </button>
          {cliente.tiene_logo && (
            <button type="button" onClick={quitarLogo} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-background border shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100" title="Quitar logo">
              <X className="w-3 h-3" />
            </button>
          )}
          <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => cambiarLogo(e.target.files?.[0])} />
        </div>
        <div className="min-w-0 flex-1">
          {editAlias ? (
            <Input
              autoFocus
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              onBlur={guardarAlias}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") {
                  setAlias(cliente.alias || "");
                  setEditAlias(false);
                }
              }}
              placeholder={cliente.nombre || "Nombre para el equipo"}
              className={cn("h-10 font-semibold", compact ? "text-base" : "text-xl")}
            />
          ) : (
            <button type="button" onClick={() => setEditAlias(true)} className="text-left w-full rounded-md hover:bg-muted/60 px-1 -mx-1" title="Cambiar el nombre con el que lo reconocéis">
              <h2 className={cn("font-semibold tracking-tight truncate", compact ? "text-base" : "text-xl")}>{nombreVisible(cliente)}</h2>
            </button>
          )}
          {legal && <p className="text-xs text-muted-foreground truncate mt-0.5 px-1 -mx-1">{legal}</p>}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <button
              type="button"
              onClick={() => guardar("estado", inactivo ? "activo" : "inactivo").catch(() => {})}
              className={cn("text-[11px] px-2 py-0.5 rounded-full border", inactivo ? "bg-slate-100 text-slate-600 border-slate-200" : "bg-emerald-50 text-emerald-700 border-emerald-200")}
              title="Cambiar estado"
            >
              {inactivo ? "Inactivo" : "Activo"}
            </button>
            {cliente.zoho_id && <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Zoho</span>}
            {cliente.tipo === "particular" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Particular</span>}
          </div>
        </div>
      </div>

      <Seccion
        titulo="Datos fiscales"
        accion={
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1.5" onClick={() => copiar(datosFiscalesTexto(cliente), "Datos fiscales copiados")}>
            <Copy className="w-3 h-3" /> Copiar todo
          </Button>
        }
      >
        <Campo label="Razón social" campo="razon_social" value={cliente.razon_social} onSave={guardar} />
        <Campo label="NIF/CIF" campo="nif" value={cliente.nif} onSave={guardar} mono />
        <Campo label="Dirección" campo="direccion" value={cliente.direccion} onSave={guardar} />
        <Campo label="C. postal" campo="codigo_postal" value={cliente.codigo_postal} onSave={guardar} />
        <Campo label="Ciudad" campo="ciudad" value={cliente.ciudad} onSave={guardar} />
        <Campo label="Provincia" campo="provincia" value={cliente.provincia} onSave={guardar} />
        <Campo label="País" campo="pais" value={cliente.pais} onSave={guardar} />
      </Seccion>

      <Seccion titulo="Contacto">
        <Campo label="Teléfono" campo="telefono" value={cliente.telefono} onSave={guardar} href={cliente.telefono ? `tel:${cliente.telefono.replace(/\s+/g, "")}` : ""} icon={Phone} />
        <Campo label="Email" campo="email" value={cliente.email} onSave={guardar} href={cliente.email ? `mailto:${cliente.email}` : ""} icon={Mail} />
        <Campo label="Web" campo="web" value={cliente.web} onSave={guardar} href={cliente.web ? (cliente.web.startsWith("http") ? cliente.web : `https://${cliente.web}`) : ""} />
      </Seccion>

      <Seccion
        titulo="Enlaces de interés"
        accion={
          !nuevoEnlace && (
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1.5" onClick={() => setNuevoEnlace({ titulo: "", url: "" })}>
              <Plus className="w-3 h-3" /> Añadir
            </Button>
          )
        }
      >
        {(cliente.enlaces || []).length > 0 ? (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {(cliente.enlaces || []).map((e) => (
              <EnlaceChip key={e.id} enlace={e} onQuitar={() => borrarEnlace(e.id)} />
            ))}
          </div>
        ) : (
          !nuevoEnlace && <p className="text-xs text-muted-foreground px-1 py-2">Drive, Figma, web en desarrollo, accesos… todo a mano.</p>
        )}
        {nuevoEnlace && (
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <Input autoFocus placeholder="Título (opcional)" value={nuevoEnlace.titulo} onChange={(e) => setNuevoEnlace((p) => ({ ...p, titulo: e.target.value }))} className="h-9 sm:w-40" />
            <Input
              placeholder="https://…"
              value={nuevoEnlace.url}
              onChange={(e) => setNuevoEnlace((p) => ({ ...p, url: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && guardarEnlace()}
              className="h-9 flex-1"
            />
            <div className="flex gap-1">
              <Button type="button" size="icon" className="h-9 w-9" onClick={guardarEnlace}><Check className="w-4 h-4" /></Button>
              <Button type="button" size="icon" variant="ghost" className="h-9 w-9" onClick={() => setNuevoEnlace(null)}><X className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Seccion>

      <Seccion titulo="Notas">
        <Textarea
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          onBlur={() => notas !== (cliente.notas || "") && guardar("notas", notas).catch(() => {})}
          placeholder="Lo que conviene saber de este cliente…"
          className="min-h-[96px] text-sm"
        />
      </Seccion>
    </div>
  );
}
